/**
 * API client for making authenticated requests to the Hono server.
 *
 * Ported from v1's `src/utils/api/apiClient.ts` with full token lifecycle:
 * 1. Token is fetched from db.cloud.currentUser (BehaviorSubject)
 * 2. accessTokenExpiration is checked with a 60s buffer
 * 3. If token is expired/expiring, refresh via a pull db.cloud.sync() (syncToRenewToken)
 * 4. Refreshed token is cached (dedup guard prevents parallel refreshes)
 * 5. On 401 "jwt expired" → clear cache and retry once
 * 6. On startup, waits for currentUser to become available (up to 5s)
 * 7. Subscribes to currentUser changes to auto-invalidate cache
 *
 * Uses browser fetch on web and CapacitorHttp on native.
 */

import { Capacitor, CapacitorHttp } from "@capacitor/core"
import { db } from "@/db/schema"
import { isTauriDesktop } from "@/shared/utils/platform/desktopDetection"
import { isIcalPath, stripQuery, summarizeRedacted } from "./logRedaction"

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * API URL resolution for different platforms:
 * - Web: relative "/api" (dev) or environment variable
 * - Capacitor: window.location.origin is "capacitor://localhost" (invalid).
 *   Relative paths fail. Use absolute URL (production API).
 * - Tauri: window.location.origin is "tauri://localhost" (invalid).
 *   Same as Capacitor — need absolute URL.
 */
export const API_PREFIX: string = (() => {
  // Check Tauri first (must come before Capacitor check)
  if (isTauriDesktop()) {
    return import.meta.env.VITE_API_BASE_URL_NATIVE
      ?? "https://api.totodo.io"
  }

  const envUrl = import.meta.env.VITE_API_BASE_URL ?? "/api"
  if (Capacitor.isNativePlatform() && envUrl.startsWith("/")) {
    // Relative path on Capacitor native → use the absolute fallback
    return import.meta.env.VITE_API_BASE_URL_NATIVE
      ?? "https://api.totodo.io"
  }
  return envUrl
})()

// ---------------------------------------------------------------------------
// Token cache
// ---------------------------------------------------------------------------

interface CachedToken {
  token: string
  /** Unix timestamp in ms */
  expiration: number
}

const ONE_HOUR_MS = 60 * 60 * 1000
const BUFFER_MS = 60 * 1000 // refresh 60s before actual expiry

let tokenCache: CachedToken | null = null
let tokenRefreshPromise: Promise<string | null> | null = null

/** Clear the token cache (called on observable change + 401 retry). */
export function clearTokenCache(): void {
  tokenCache = null
  tokenRefreshPromise = null
}

/** Reset the refresh cooldown (called on 401 retry to allow immediate refresh). */
export function resetRefreshCooldown(): void {
  lastRefreshFailureAt = 0
  consecutiveRefreshFailures = 0
}

/**
 * Renew the Dexie Cloud access token by running a sync. Must be a PULL: a push
 * sync returns before it loads the token when nothing local is waiting to be
 * pushed (`purpose === 'push' && !pushSyncIsNeeded` in the addon), so on an app
 * with no pending changes the token was never renewed and every API call
 * answered 401 until the WebSocket happened to reconnect (finding A18).
 */
export function syncToRenewToken(): Promise<void> {
  return db.cloud.sync({ purpose: "pull", wait: true })
}

// ---------------------------------------------------------------------------
// Observable subscription — auto-invalidate cache on token change
// ---------------------------------------------------------------------------

let subscriptionInitialized = false

function subscribeToCurrentUserChanges(): void {
  if (subscriptionInitialized) return
  subscriptionInitialized = true

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const observable = db.cloud.currentUser as any
    if (!observable?.subscribe) return

    let lastToken = observable.value?.accessToken ?? null

    const sub = observable.subscribe(
      (value: { accessToken?: string } | null) => {
        const nextToken = value?.accessToken ?? null
        if (nextToken === lastToken) return
        lastToken = nextToken
        clearTokenCache()
      },
    )

    // Handle both rxjs Subscription and plain unsubscribe function
    if (typeof sub === "function") {
      // plain unsubscribe — store but don't call
    } else if (sub && typeof sub.unsubscribe === "function") {
      // rxjs Subscription — store but don't call
    }
  } catch {
    // No observable support — ignore
  }
}

// Initialize subscription on module load
subscribeToCurrentUserChanges()

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Coerce Date/string/number → number (ms epoch) or undefined. */
function toTimestamp(v: unknown): number | undefined {
  if (v instanceof Date) {
    const t = v.getTime()
    return Number.isFinite(t) ? t : undefined
  }
  if (typeof v === "number" && Number.isFinite(v)) return v
  if (typeof v === "string") {
    const n = Number(v)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

/**
 * Wait for db.cloud.currentUser to have an accessToken.
 * Handles startup race condition where Dexie Cloud hasn't loaded yet.
 */
async function waitForCurrentUser(timeoutMs = 5000): Promise<boolean> {
  // Fast path — already available
  if (db.cloud.currentUser?.value?.accessToken) return true

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const observable = db.cloud.currentUser as any

  if (observable?.subscribe) {
    return new Promise<boolean>((resolve) => {
      let resolved = false
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let cleanup: (() => void) | null = null

      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true
          cleanup?.()
          resolve(false)
        }
      }, timeoutMs)

      const sub = observable.subscribe(
        (value: { accessToken?: string } | null) => {
          if (value?.accessToken && !resolved) {
            resolved = true
            clearTimeout(timeout)
            cleanup?.()
            resolve(true)
          }
        },
      )

      if (typeof sub === "function") {
        cleanup = sub
      } else if (sub && typeof sub.unsubscribe === "function") {
        cleanup = () => sub.unsubscribe()
      }

      // Re-check in case value arrived between first check and subscribe
      if (observable.value?.accessToken && !resolved) {
        resolved = true
        clearTimeout(timeout)
        cleanup?.()
        resolve(true)
      }
    })
  }

  // Fallback: poll
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (db.cloud.currentUser?.value?.accessToken) return true
    await new Promise((r) => setTimeout(r, 100))
  }
  return false
}

// ---------------------------------------------------------------------------
// getValidToken — the core token lifecycle logic
// ---------------------------------------------------------------------------

/** Exponential backoff after failed token refreshes — avoid hammering Dexie Cloud. */
let lastRefreshFailureAt = 0
let consecutiveRefreshFailures = 0
const BASE_COOLDOWN_MS = 30_000 // 30s after first failure
const MAX_COOLDOWN_MS = 10 * 60_000 // 10 min cap

function getRefreshCooldownMs(): number {
  if (consecutiveRefreshFailures <= 0) return 0
  // 30s, 60s, 120s, 240s, 480s, capped at 600s
  return Math.min(BASE_COOLDOWN_MS * Math.pow(2, consecutiveRefreshFailures - 1), MAX_COOLDOWN_MS)
}

export async function getValidToken(): Promise<string | null> {
  // Deduplicate concurrent refresh calls
  if (tokenRefreshPromise) return tokenRefreshPromise

  let currentUser = db.cloud.currentUser

  // Startup wait
  if (!currentUser?.value?.accessToken) {
    const available = await waitForCurrentUser(3000)
    if (!available) return null
    currentUser = db.cloud.currentUser
  }

  if (!currentUser?.value?.accessToken) return null

  const { accessToken, accessTokenExpiration } = currentUser.value
  const expiration = toTimestamp(accessTokenExpiration) ?? 0
  const now = Date.now()

  // Return cached token if still valid — may differ from currentUser.value
  // after a direct /token refresh (401 retry path)
  if (
    tokenCache &&
    tokenCache.expiration > now + BUFFER_MS
  ) {
    return tokenCache.token
  }

  // Clear stale cache
  if (
    tokenCache &&
    tokenCache.expiration <= now
  ) {
    tokenCache = null
  }

  // If token is expired or expires within BUFFER_MS → refresh
  if (expiration === 0 || expiration <= now + BUFFER_MS) {
    // Cooldown: exponential backoff if refresh recently failed
    const cooldown = getRefreshCooldownMs()
    if (lastRefreshFailureAt && now - lastRefreshFailureAt < cooldown) {
      // Return expired token as last resort — let server 401 trigger retry
      return accessToken
    }

    tokenRefreshPromise = (async () => {
      try {
        const syncPromise = syncToRenewToken()
        const timeoutPromise = new Promise<void>((_, reject) =>
          setTimeout(
            () => reject(new Error("Token refresh timed out")),
            15_000,
          ),
        )
        await Promise.race([syncPromise, timeoutPromise])

        const refreshed = db.cloud.currentUser?.value
        if (refreshed?.accessToken) {
          const newExp =
            toTimestamp(refreshed.accessTokenExpiration) ?? now + ONE_HOUR_MS
          tokenCache = { token: refreshed.accessToken, expiration: newExp }
          lastRefreshFailureAt = 0
          consecutiveRefreshFailures = 0 // reset on success
          return refreshed.accessToken
        }
        lastRefreshFailureAt = Date.now()
        consecutiveRefreshFailures++
        // Return expired token as last resort — 401 retry will attempt another refresh
        return accessToken
      } catch {
        // Fas 6b: Even if sync failed, the token may have been refreshed
        // (token refresh can succeed while data sync fails)
        const postFailUser = db.cloud.currentUser?.value
        if (postFailUser?.accessToken && postFailUser.accessToken !== accessToken) {
          const newExp =
            toTimestamp(postFailUser.accessTokenExpiration) ?? Date.now() + ONE_HOUR_MS
          if (newExp > Date.now() + BUFFER_MS) {
            tokenCache = { token: postFailUser.accessToken, expiration: newExp }
            lastRefreshFailureAt = 0
            consecutiveRefreshFailures = 0
            return postFailUser.accessToken
          }
        }
        lastRefreshFailureAt = Date.now()
        consecutiveRefreshFailures++
        // Return expired token as last resort — 401 retry will attempt another refresh
        return accessToken
      } finally {
        tokenRefreshPromise = null
      }
    })()

    return tokenRefreshPromise
  }

  // Token is valid — cache and return
  tokenCache = {
    token: accessToken,
    expiration: expiration || now + ONE_HOUR_MS,
  }
  return accessToken
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ApiRequestOptions {
  /** Path relative to API prefix, e.g. "/ical/proxy" */
  path: string
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
  params?: Record<string, string>
  body?: unknown
  headers?: Record<string, string>
  /** Timeout in ms (default 15 000) */
  timeout?: number
  /** If true, return raw Response instead of parsed JSON */
  raw?: boolean
}

export interface PublicRequestOptions {
  url: string
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
  params?: Record<string, string>
  body?: unknown
  headers?: Record<string, string>
  timeout?: number
  raw?: boolean
}

export interface ApiResponse<T = unknown> {
  ok: boolean
  status: number
  data: T
}

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH"

function redactHeaders(headers: Record<string, string> | undefined) {
  if (!headers) return {}
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      key.toLowerCase() === "authorization" ? "[redacted]" : value,
    ]),
  )
}

function logNativeRequestStart(options: PublicRequestOptions, url: string) {
  if (isIcalPath(url)) {
    console.log("[ApiClient][native] request", { method: options.method ?? "GET", url: stripQuery(url) })
    return
  }
  console.log("[ApiClient][native] request", {
    method: options.method ?? "GET",
    url,
    headers: redactHeaders(options.headers),
    body: summarizeRedacted(options.body),
    timeout: options.timeout ?? 15_000,
  })
}

function logNativeRequestResponse(
  options: PublicRequestOptions,
  url: string,
  status: number,
  data: unknown,
) {
  const level = status >= 200 && status < 300 ? "log" : "warn"
  if (isIcalPath(url)) {
    console[level]("[ApiClient][native] response", { method: options.method ?? "GET", url: stripQuery(url), status })
    return
  }
  console[level]("[ApiClient][native] response", {
    method: options.method ?? "GET",
    url,
    status,
    data: summarizeRedacted(data),
  })
}

function logNativeRequestError(
  options: PublicRequestOptions,
  url: string,
  error: unknown,
) {
  if (isIcalPath(url)) {
    console.error("[ApiClient][native] error", { method: options.method ?? "GET", url: stripQuery(url) })
    return
  }
  console.error("[ApiClient][native] error", {
    method: options.method ?? "GET",
    url,
    error: summarizeRedacted(
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : error,
    ),
  })
}

function buildUrl(baseUrl: string, params?: Record<string, string>): string {
  const absoluteUrl = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(baseUrl)
    ? baseUrl
    : new URL(baseUrl, window.location.origin).toString()

  if (!params || Object.keys(params).length === 0) return absoluteUrl

  const url = new URL(absoluteUrl)
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value)
  }
  return url.toString()
}

function normalizeResponseHeaders(
  headers: Headers | Record<string, string | undefined> | undefined,
): Record<string, string> {
  if (!headers) return {}
  if (headers instanceof Headers) {
    const result: Record<string, string> = {}
    headers.forEach((value, key) => {
      result[key.toLowerCase()] = value
    })
    return result
  }
  return Object.fromEntries(
    Object.entries(headers)
      .filter(([, value]) => typeof value === "string")
      .map(([key, value]) => [key.toLowerCase(), value as string]),
  )
}

function normalizePayload<T>(
  payload: unknown,
  contentType: string,
  raw: boolean | undefined,
): T {
  if (raw) return payload as T

  if (
    contentType.includes("text/calendar") ||
    contentType.includes("text/plain")
  ) {
    if (typeof payload === "string") return payload as T
    return JSON.stringify(payload ?? "") as T
  }

  if (typeof payload === "string") {
    const trimmed = payload.trim()
    if (!trimmed) return null as T
    if (
      contentType.includes("application/json") ||
      trimmed.startsWith("{") ||
      trimmed.startsWith("[")
    ) {
      try {
        return JSON.parse(trimmed) as T
      } catch {
        return payload as T
      }
    }
  }

  return payload as T
}

async function executeRequest<T>(
  options: PublicRequestOptions,
): Promise<ApiResponse<T>> {
  const url = buildUrl(options.url, options.params)
  const method: RequestMethod = options.method ?? "GET"
  const timeoutMs = options.timeout ?? 15_000
  const headers = options.headers ?? {}

  if (Capacitor.isNativePlatform()) {
    logNativeRequestStart(options, url)
    try {
      const response = await CapacitorHttp.request({
        url,
        method,
        headers,
        data: options.body,
        connectTimeout: timeoutMs,
        readTimeout: timeoutMs,
      })

      const normalizedHeaders = normalizeResponseHeaders(response.headers)
      const contentType = normalizedHeaders["content-type"] ?? ""
      const data = normalizePayload<T>(response.data, contentType, options.raw)

      logNativeRequestResponse(options, url, response.status, data)

      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        data,
      }
    } catch (error) {
      logNativeRequestError(options, url, error)
      throw error
    }
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(url, {
      method,
      headers,
      body:
        options.body === undefined
          ? undefined
          : headers["Content-Type"] === "application/json" ||
              headers["content-type"] === "application/json"
            ? JSON.stringify(options.body)
            : (options.body as BodyInit),
      signal: controller.signal,
    })

    const contentType = response.headers.get("content-type") ?? ""

    if (options.raw) {
      return {
        ok: response.ok,
        status: response.status,
        data: response as unknown as T,
      }
    }

    let payload: unknown = null
    if (
      contentType.includes("text/calendar") ||
      contentType.includes("text/plain")
    ) {
      payload = await response.text()
    } else {
      payload = await response.text()
    }

    return {
      ok: response.ok,
      status: response.status,
      data: normalizePayload<T>(payload, contentType, false),
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(`API request timed out after ${timeoutMs}ms: ${url}`)
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}

export async function publicRequest<T = unknown>(
  options: PublicRequestOptions,
): Promise<ApiResponse<T>> {
  return executeRequest<T>(options)
}

function extractErrorMessage(data: unknown): string {
  if (!data || typeof data !== "object") return ""
  const record = data as Record<string, unknown>
  if (typeof record.message === "string") return record.message
  if (typeof record.error === "string") return record.error
  return ""
}

/**
 * Make an authenticated request to the Hono API server.
 */
export async function apiRequest<T = unknown>(
  options: ApiRequestOptions,
): Promise<ApiResponse<T>> {
  return apiRequestInternal<T>(options, false)
}

async function apiRequestInternal<T>(
  options: ApiRequestOptions,
  isRetry: boolean,
): Promise<ApiResponse<T>> {
  const token = await getValidToken()
  if (!token) {
    throw new Error("Not authenticated — no Dexie Cloud access token")
  }

  // Build URL
  const url = `${API_PREFIX}${options.path}`

  try {
    const res = await executeRequest<T | Response>({
      url,
      params: options.params,
      method: options.method ?? "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers,
      },
      body: options.body,
      timeout: options.timeout,
      raw: options.raw,
    })

    // ── 401 retry (once) ──
    if (res.status === 401 && !isRetry) {
      const msg = extractErrorMessage(res.data)
      if (
        msg.includes("jwt expired") ||
        msg.includes("Token verification failed") ||
        msg.includes("Invalid token")
      ) {
        if (Capacitor.isNativePlatform()) {
          console.warn("[ApiClient][native] retrying after token/auth failure", {
            path: stripQuery(options.path),
            status: res.status,
            message: msg,
          })
        }
        // Force a Dexie Cloud sync to get a fresh token.  This updates
        // db.cloud.currentUser (the observable), which in turn triggers
        // useTokenSync → Capacitor Preferences → native background gets
        // the new token too.  Clearing the in-memory cache alone is not
        // enough because currentUser.value still holds the same
        // (server-rejected) token and the retry would re-send it.
        clearTokenCache()
        // Reset cooldown so retry's getValidToken() can attempt a fresh refresh
        lastRefreshFailureAt = 0
        consecutiveRefreshFailures = 0
        let syncRefreshed = false
        try {
          await Promise.race([
            db.cloud.sync({ purpose: "push", wait: true }),
            new Promise<void>((_, reject) =>
              setTimeout(() => reject(new Error("Token refresh timed out")), 10_000),
            ),
          ])
          syncRefreshed = true
        } catch {
          // Sync-based refresh failed (sync lock held) — try direct token refresh
        }
        if (!syncRefreshed) {
          // Direct fallback: call Dexie Cloud /token endpoint without sync lock
          const DEXIE_CLOUD_URL = import.meta.env.VITE_DEXIE_CLOUD_URL || "https://zfbftppyv.dexie.cloud"
          const refreshTkn = (db.cloud.currentUser?.value as { refreshToken?: string })?.refreshToken
          if (refreshTkn) {
            try {
              const tokenRes = await fetch(`${DEXIE_CLOUD_URL}/token`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ grant_type: "refresh_token", refresh_token: refreshTkn, scopes: ["ACCESS_DB"], time_stamp: Date.now() }),
                signal: AbortSignal.timeout(10_000),
              })
              if (tokenRes.ok) {
                const d = await tokenRes.json() as { accessToken?: string; accessTokenExpiration?: number }
                if (d.accessToken) {
                  const exp = toTimestamp(d.accessTokenExpiration) ?? Date.now() + ONE_HOUR_MS
                  tokenCache = { token: d.accessToken, expiration: exp }
                }
              }
            } catch { /* direct refresh also failed */ }
          }
        }
        return apiRequestInternal<T>(options, true)
      }
    }

    return { ok: res.ok, status: res.status, data: res.data as T }
  } catch (err) {
    throw err
  }
}
